import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { api, resolveAssetUrl } from "../../lib/api";
import { VideoPlayer, getEmbedInfo } from "../../components/VideoPlayer";
import {
  ArrowLeft, Play, ChevronDown, ChevronUp, Clock, Layers, CheckCircle2,
} from "lucide-react";

const DEFAULT_COVER =
  "https://customer-assets.emergentagent.com/job_athletic-edge-19/artifacts/z57p91jo_IMG_2623%202.PNG";

const ExerciseRow = ({ ex }) => {
  const [open, setOpen] = useState(false);
  const url = ex.youtube_url || ex.video_url;
  const info = getEmbedInfo(url);
  const embeddable = info.kind === "youtube" || info.kind === "vimeo";
  return (
    <div className="py-3 border-b border-white/5 last:border-0" data-testid="viewer-exercise">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-white font-semibold text-sm">{ex.name}</div>
          <div className="text-neutral-400 text-xs mt-1 flex flex-wrap gap-3">
            {ex.sets && <span><b className="text-neutral-200">{ex.sets}</b> series</span>}
            {ex.reps && <span><b className="text-neutral-200">{ex.reps}</b> reps</span>}
            {ex.rest && <span>Descanso {ex.rest}</span>}
            {ex.load && <span>Carga: {ex.load}</span>}
            {ex.tempo && <span>Tempo: {ex.tempo}</span>}
            {ex.intensity && <span>Intensidad: {ex.intensity}</span>}
          </div>
          {ex.notes && <p className="text-neutral-500 text-xs mt-2 italic">{ex.notes}</p>}
        </div>
        {url && (
          embeddable ? (
            <button type="button" onClick={() => setOpen(!open)}
                    className="shrink-0 w-9 h-9 rounded-full inline-flex items-center justify-center border border-white/15 text-white hover:border-[color:var(--qv-red)] hover:text-[color:var(--qv-red)] transition-colors"
                    aria-label={open ? "Ocultar vídeo" : "Ver vídeo"}
                    data-testid="viewer-video-toggle">
              <Play size={14} style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .2s ease" }} />
            </button>
          ) : (
            <a href={url} target="_blank" rel="noreferrer"
               className="shrink-0 w-9 h-9 rounded-full inline-flex items-center justify-center border border-white/15 text-white hover:border-[color:var(--qv-red)] hover:text-[color:var(--qv-red)] transition-colors"
               aria-label="Ver vídeo (externo)">
              <Play size={14} />
            </a>
          )
        )}
      </div>
      {open && embeddable && <div className="mt-3"><VideoPlayer url={url} title={ex.name} /></div>}
    </div>
  );
};

const DayBlock = ({ day, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const totalExercises = (day.blocks || []).reduce((a, b) => a + (b.exercises?.length || 0), 0);
  return (
    <div className="border border-white/10 bg-[#0a0a0a]" data-testid={`viewer-day-${day.id}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left"
      >
        <div>
          <div className="text-[10px] tracking-[0.22em] text-neutral-500 uppercase">{day.day}</div>
          <div className="qv-display text-white text-lg mt-1">
            {day.rest ? "Descanso" : (day.title || "Sesión")}
          </div>
          {!day.rest && (
            <div className="text-[11px] text-neutral-500 mt-1">{totalExercises} ejercicios</div>
          )}
        </div>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {open && !day.rest && (
        <div className="px-5 pb-5 space-y-5">
          {(day.blocks || []).map((b) => (
            <div key={b.id}>
              <div className="qv-overline mb-2">{b.type}{b.name ? ` · ${b.name}` : ""}</div>
              <div>
                {(b.exercises || []).map((ex) => <ExerciseRow key={ex.id} ex={ex} />)}
              </div>
            </div>
          ))}
          {day.notes && (
            <div className="border-t border-white/10 pt-4 text-sm text-neutral-400 whitespace-pre-line">
              {day.notes}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function ClientProgramView() {
  const { id } = useParams();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openWeek, setOpenWeek] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/me/programs/${id}`);
        if (alive) setProgram(data);
      } catch (err) {
        if (alive) setError(err?.response?.data?.detail || "No tienes acceso a este programa.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  if (loading) {
    return <AppShell><div className="p-8 text-neutral-500">Cargando...</div></AppShell>;
  }
  if (error) {
    return (
      <AppShell>
        <div className="p-8 max-w-lg" data-testid="viewer-error">
          <div className="qv-overline">SIN ACCESO</div>
          <h2 className="qv-display text-white text-3xl mt-3">{error}</h2>
          <Link to="/app/programas" className="qv-btn-primary mt-6 inline-flex">
            <ArrowLeft size={16} /> Ver mis programas
          </Link>
        </div>
      </AppShell>
    );
  }

  const totalExercises = (program.weeks || []).reduce(
    (acc, w) => acc + (w.days || []).reduce((a, d) => a + (d.blocks || []).reduce((x, b) => x + (b.exercises?.length || 0), 0), 0), 0
  );

  return (
    <AppShell>
      <div className="max-w-5xl" data-testid="client-program-view">
        {/* Cover header */}
        <div className="relative overflow-hidden">
          <div className="aspect-[16/6] sm:aspect-[16/5] relative">
            <img src={resolveAssetUrl(program.cover_image) || DEFAULT_COVER} alt={program.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20" />
            <div className="absolute inset-0 flex items-end p-5 sm:p-8">
              <div>
                <Link to="/app/programas" className="text-[11px] tracking-[0.22em] uppercase text-neutral-300 hover:text-white inline-flex items-center gap-1.5 mb-3">
                  <ArrowLeft size={12} /> Mis programas
                </Link>
                <div className="qv-overline">{program.level || "PROGRAMA"}</div>
                <h1 className="qv-display text-white text-4xl sm:text-5xl mt-2 leading-[0.95]" data-testid="viewer-title">{program.title}</h1>
                {program.subtitle && <p className="text-neutral-300 text-sm sm:text-base mt-3 max-w-2xl">{program.subtitle}</p>}
                <div className="mt-5 flex flex-wrap gap-2">
                  <span className="qv-chip"><Clock size={12} /> {program.duration_weeks} semanas</span>
                  <span className="qv-chip"><Layers size={12} /> {totalExercises} ejercicios</span>
                  <span className="qv-chip"><CheckCircle2 size={12} color="var(--qv-wa)" /> Comprado</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-5 sm:p-8">
          {(program.weeks || []).length === 0 && (
            <div className="text-neutral-500">Este programa aún no tiene contenido.</div>
          )}

          <div className="space-y-3">
            {(program.weeks || []).map((w, wi) => (
              <div key={w.id} className="border border-white/10 bg-[#080808]" data-testid={`viewer-week-${wi}`}>
                <button
                  type="button"
                  onClick={() => setOpenWeek(openWeek === wi ? -1 : wi)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <div>
                    <div className="text-[10px] tracking-[0.22em] text-neutral-500 uppercase">Semana {w.week_number}</div>
                    {w.title && <div className="qv-display text-white text-xl mt-1">{w.title}</div>}
                    {w.subtitle && <div className="text-neutral-400 text-sm mt-0.5">{w.subtitle}</div>}
                  </div>
                  {openWeek === wi ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                {openWeek === wi && (
                  <div className="p-4 space-y-3 border-t border-white/5">
                    {(w.days || []).map((d) => <DayBlock key={d.id} day={d} defaultOpen={false} />)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
